import { Component, ElementRef, EventEmitter, Injectable, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController, Platform } from '@ionic/angular';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { PermissionItem, TPermissionType } from 'src/app/interfaces/PWA/Permissions';
import { FileService } from '../File/file.service';


const PERMISSIONS: TPermissionType[] = ['camera', 
// 'geolocation'
];


@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  private readonly permissionsSubject = new BehaviorSubject<PermissionItem[]>([]);
  permissions$ = this.permissionsSubject.asObservable();
  permissionsModalOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  allPermissionsGranted$ = this.permissions$.pipe(
    map((permissions) => permissions.every((p) => p.state === 'granted'))
  );
  permissionChange$: EventEmitter<PermissionItem> = new EventEmitter();


  intervalId: any;
  permissionsChanges$: Observable<PermissionItem>;
  text = new BehaviorSubject<string>('');

  constructor(
    private readonly permissionsModalController: ModalController,
    private readonly fileService: FileService,
    private readonly platform: Platform,
  ) {
  }

  public async initializePermissionChangeObservables() {
    const permissionItems = [];
    for (const permission of PERMISSIONS) {
      const permissionStatus = await this.checkPermission(permission);
      permissionItems.push({ name: permission, state: permissionStatus.state });
    }
    this.text.next('iniciaaal')
    this.permissionsSubject.next(permissionItems);
    this.permissionChange$.subscribe((permissionItem) => {
      this.onPermissionChange(permissionItem);
    });
    let timer = 0;
    setInterval(async () => {
      let text = ''
      for (const permission of this.permissionsSubject.value) {
        const permissionStatus = await this.checkPermission(permission.name);
        text += `${permission.name}: ${permissionStatus.state}\n`
        if (permission.state !== permissionStatus.state) {
          this.permissionChange$.emit({ name: permission.name, state: permissionStatus.state });
        }
      }
      timer += 1;
      this.text.next(text + `Segundos: ${timer}`);
    }, 1000);
    this.openPermissionsModal();
  }

  private async checkPermission(permission: TPermissionType): Promise<PermissionStatus> {
    return await navigator.permissions.query({ name: permission as PermissionName });
  }

  private onPermissionChange(
    permissionItem: PermissionItem,
  ) {
    const currentPermissions = [...this.permissionsSubject.getValue() ];
    const index = currentPermissions.findIndex(item => item.name === permissionItem.name);
    currentPermissions[index].state = permissionItem.state;
    this.permissionsSubject.next(currentPermissions);
  }

  public async requestPermission(permission: TPermissionType) {
    let state: PermissionState = 'prompt';
    try {
      if (permission === 'camera') {
        state = await this.requestCameraPermission();
      } else if (permission === 'geolocation') {
        state = await this.requestGeolocationPermission();
      } else {
        state = 'denied';
      }
    } catch (error) {
      console.error('Error requesting geolocation permission:', error);
      state = 'denied';
    }
    this.permissionChange$.emit({ name: permission, state });
  }

  private async requestCameraPermission(): Promise<PermissionState> {
    return new Promise<PermissionState>((resolve, reject) => {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then((stream) => {
          this.fileService.stopStream(stream);
          resolve('granted');
        })
        .catch((error) => {
          resolve('denied');
        });
    });
  }

  private async requestGeolocationPermission(): Promise<PermissionState> {
    return new Promise<PermissionState>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.text.next('granted');
          resolve('granted');
        },
        (error) => {
          this.text.next('error: ' + error.message);
          resolve('denied');
        },
      );
    });
  }

  private async openPermissionsModal() {
    if (this.permissionsModalOpen$.value) return;

    this.permissionsModalOpen$.next(true);

    const modal = await this.permissionsModalController.create({
      component: PermissionsModalComponent,
      cssClass: 'permissions-modal',
      backdropDismiss: false,
    });

    modal.onDidDismiss().then(() => {
      this.permissionsModalOpen$.next(false);
    });

    await modal.present();
  }
}


@Component({
  selector: 'app-permissions-modal',
  templateUrl: './permissions.component.html',
  styleUrls: ['./permissions.component.scss'],
})
export class PermissionsModalComponent implements OnInit {
  permissionGuideText = "El permiso ha sido denegado, por favor actívalo en la configuración de tu dispositivo o navegador.";
  // private readonly subscription: Subscription | null = null;
  @ViewChild('camera') cameraRef: ElementRef | undefined;
  cameraStatus: 'on' | 'off' | 'not-allowed' = 'not-allowed'

  constructor(
    private readonly modalController: ModalController,
    private readonly permissionsService: PermissionsService
  ) { }

  
  get permissions() {
    return this.permissionsService.permissions$;
  }

  get allPermissionsGranted() {
    return this.permissionsService.allPermissionsGranted$;
  }


  get textRef() {
    return this.permissionsService.text;
  }

  ngOnInit(): void {
    this.cameraRef!.nativeElement.hidden = true;
    this.permissions.subscribe((permissions) => {
      const cameraPermission = permissions.find((p) => p.name === 'camera');
      if (cameraPermission?.state === 'granted') {
        this.cameraStatus = 'off';
      } else {
        this.cameraStatus = 'not-allowed';
      }
    });
  }

  async requestPermission(permission: PermissionItem) {
    if (permission.state !== 'prompt') return;
    this.permissionsService.requestPermission(permission.name);
  }

  startCamera() {
    if (!this.cameraRef) return;
    const video = this.cameraRef.nativeElement as HTMLVideoElement;
    navigator.mediaDevices.getUserMedia({ video: true })
      .then((stream) => {
        video.srcObject = stream;
        video.hidden = false;
      })
      .catch((error) => {
        console.error('Error starting camera:', error);
      });
  }

  stopCamera() {
    if (!this.cameraRef) return;
    const video = this.cameraRef.nativeElement as HTMLVideoElement;
    const stream = video.srcObject as MediaStream;
    stream.getTracks().forEach((track) => track.stop());
    video.hidden = true;
  }


  getPermissionText(permission: TPermissionType) {
    switch (permission) {
      case 'camera':
        return 'Cámara';
      case 'geolocation':
        return 'Ubicación';
      default:
        return 'Permiso';
    }
  }

  getPermissionIcon(permission: TPermissionType) {
    switch (permission) {
      case 'camera':
        return 'camera';
      case 'geolocation':
        return 'locate';
      default:
        return 'help';
    }
  }


  closeModal() {
    this.modalController.dismiss();
  }
}
